import React, { useEffect, useState } from 'react';
import { App, Form, Input, Modal } from 'antd';
import salesOrderService from '@/services/salesOrder.service';

type Props = {
  open: boolean;
  salesOrderId: string;
  salesOrderNumber?: string;
  billingAddress?: string | null;
  shippingAddress?: string | null;
  okText?: string;
  onClose: () => void;
  /** Runs after the addresses are saved (e.g. continue with approval). */
  onSaved: () => void | Promise<void>;
};

type FormValues = { billing_address?: string; shipping_address: string };

export default function SalesOrderAddressModal(props: Props) {
  const { message } = App.useApp();
  const [form] = Form.useForm<FormValues>();
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!props.open) return;
    form.setFieldsValue({
      billing_address: props.billingAddress || '',
      shipping_address: props.shippingAddress || '',
    });
  }, [props.open, props.billingAddress, props.shippingAddress, form]);

  const submit = async (values: FormValues) => {
    setSaving(true);
    try {
      await salesOrderService.updateAddresses(props.salesOrderId, {
        billing_address: values.billing_address?.trim() || undefined,
        shipping_address: values.shipping_address.trim(),
      });
      message.success('Addresses saved');
      await props.onSaved();
      props.onClose();
    } catch (e: any) {
      message.error(e?.response?.data?.message || e?.message || 'Failed to save addresses');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={`Billing & shipping address${props.salesOrderNumber ? ` — ${props.salesOrderNumber}` : ''}`}
      open={props.open}
      onCancel={() => !saving && props.onClose()}
      onOk={() => form.submit()}
      okText={props.okText || 'Save'}
      okButtonProps={{ loading: saving }}
      cancelButtonProps={{ disabled: saving }}
      destroyOnClose
    >
      <Form form={form} layout="vertical" onFinish={submit}>
        <Form.Item name="billing_address" label="Billing Address">
          <Input.TextArea rows={3} placeholder="Billing address" />
        </Form.Item>
        <Form.Item
          name="shipping_address"
          label="Shipping Address"
          rules={[{ required: true, whitespace: true, message: 'Shipping Address is required for approval' }]}
        >
          <Input.TextArea rows={3} placeholder="Shipping / delivery address" />
        </Form.Item>
      </Form>
    </Modal>
  );
}
